import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {DomSanitizer} from "@angular/platform-browser";
import {ActivatedRoute, Router} from "@angular/router";
import {faEnvelope, faImage} from "@fortawesome/free-regular-svg-icons";
import {faCalendarAlt, faCartArrowDown, faClock, faCropAlt, faDownload, faEdit, faEllipsisV, faEye, faHandPointer, faPen, faPlus, faTag, faTrashAlt, faUnlink} from "@fortawesome/free-solid-svg-icons";
import {TranslateService} from "@ngx-translate/core";
import {SwalComponent, SwalPortalTargets} from "@sweetalert2/ngx-sweetalert2";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {UserService} from "app/core/services/admin/user/user.service";
import {DataModelService} from "app/core/services/dam/datamodel.service";
import {MediaService} from "app/core/services/dam/media.service";
import {SwalModalService} from "app/core/services/global/modal/modal.service";
import {ToastService} from "app/core/services/global/toast/toast.service";
import {TagService} from "app/core/services/tag/tag.service";
import {UploadService} from "app/core/services/upload/upload.service";
import {CropperComponent} from "app/shared/components/cropper/cropper.component";
import {MediaType} from "app/shared/helpers/media-type.helper";
import {ThumbButtons} from "app/shared/helpers/thumb-buttons";
import {MaestroTags} from "app/shared/models";
import {ACL} from "app/shared/models/acl";
import {Thumb} from "app/shared/models/thumb";
import {saveAs} from "file-saver";
import {map} from "rxjs/operators";
import {environment} from "src/environments";
import {PreviewService} from "../../../../core/services/thumbnails/preview.service";
import {ThumbnailsService} from "../../../../core/services/thumbnails/thumbnails.service";

interface GetThumbParams {
    binImage: any;
}

// Type minimal de ResizeObserver (absent de la lib DOM du tsconfig Angular 11 ;
// le runtime est bien présent dans les navigateurs ciblés).
interface KeywordsResizeObserver {
    observe(target: Element): void;
    disconnect(): void;
}
declare const ResizeObserver: {
    new (callback: () => void): KeywordsResizeObserver;
};

// Constantes de mesure de la zone mots-clés (px). Centralisées (DRY) car
// partagées entre la détection de débordement et le remplissage des 2 lignes.
const TAG_GAP = 5; // espace horizontal entre chips (mr-1 = 4px + 1px de marge)
const TAG_ADD_BTN_WIDTH = 24; // largeur du bouton « + » (mesuré ≈ 21px)
const TAG_MORE_BADGE_WIDTH = 72; // largeur du badge « +N autres » (mesuré ≈ 65-70px)
const TAG_MIN_CHIP_WIDTH = 48; // largeur mini pour afficher un chip tronqué (sinon on n'en met pas)

@Component({
    selector: "[app-thumb]",
    templateUrl: "./thumb.component.html",
    styleUrls: ["./thumb.component.scss"],
})
export class ThumbComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() media: Thumb;
    @Input() buttons: ThumbButtons;
    @Input() selectable: boolean = false;
    @Input() selectedMedias: number[] = [];
    @Input() linkedMedias: any[] = [];
    @Input() minified: boolean = false; // Simplify interface
    @Input() module: string = "dam";
    @Input() singleSelection: boolean = false;
    @Input() linkAdded: boolean = false;

    damUrl: string;

    readonly faTrashAlt = faTrashAlt;
    readonly faEdit = faEdit;
    readonly faEye = faEye;
    readonly faPlus = faPlus;
    readonly faUnlink = faUnlink;
    readonly faImage = faImage;
    readonly faBucket = faCartArrowDown;
    readonly faEmail = faEnvelope;
    readonly faCrop = faCropAlt;
    readonly faHandPointer = faHandPointer;
    readonly faDownload = faDownload;
    readonly faEllipsisV = faEllipsisV;
    readonly faTag = faTag;
    readonly faCalendar = faCalendarAlt;
    readonly faClock = faClock;
    readonly faPen = faPen;

    /** Édition inline du nom dans la pop-up d'édition. */
    editingName = false;

    formAddTag: FormGroup;
    formRename: FormGroup;
    dataModelForm: FormGroup;
    isValid: boolean;

    @Output() public unlinked = new EventEmitter<number>();
    @Output() public openModal = new EventEmitter<Thumb>();
    @Output() public deleted = new EventEmitter<number>();
    @Output() public addCart = new EventEmitter<Thumb>();
    @Output() public removeCart = new EventEmitter<Thumb>();
    @Output() public tagFiltered = new EventEmitter<any>();
    @Output() public tagRemoved = new EventEmitter<void>();
    @Output() public thumbUpdateEvent = new EventEmitter<number>();
    @Output() public selectMedia: EventEmitter<any> = new EventEmitter();
    @Output() public selectExtension: EventEmitter<any> = new EventEmitter();
    @Output() public openCrop: EventEmitter<any> = new EventEmitter();

    @ViewChild("changeMedia") public changeMediaSwal: SwalComponent;
    @ViewChild("addTagModal") public addTagModal: SwalComponent;
    @ViewChild("renameModal") public renameModal: SwalComponent;
    @ViewChild("editFormModal") public editFormModal: TemplateRef<any>;
    @ViewChild("cropper") public cropper: CropperComponent;
    @ViewChild("datamodel") public dataModelModal: SwalComponent;

    // Conteneur de la zone mots-clés : sert à mesurer combien de chips tiennent sur 2 lignes.
    @ViewChild("keywordsList") private keywordsListRef?: ElementRef<HTMLElement>;

    /**
     * Nombre de chips de tags affichés dans la zone mots-clés (2 lignes max).
     * Calculé par mesure (computeVisibleTags) ; le reste est résumé par « +N autres ».
     * Initialisé très haut pour tout rendre au 1er paint, puis réduit après mesure.
     */
    visibleTagCount: number = Number.MAX_SAFE_INTEGER;

    /**
     * Largeur max (px) imposée aux chips tronqués pour remplir la fin de ligne,
     * indexée par la position du tag. Vide = aucun chip tronqué (largeur naturelle).
     */
    tagMaxWidths: {[index: number]: number} = {};

    private _keywordsResizeObserver?: KeywordsResizeObserver;

    acceptList: string;
    acl: ACL;
    @Input() dataModels = [];

    @Input() tags: MaestroTags = [];
    filteredTags = [];

    @Input() cart: number[] = []; // ids des médias déjà dans le panier

    get canAddTag(): boolean {
        return this.acl.SYSPAD_DAM_TAG_UPDATE && window.location.href.includes("dam");
    }

    /**
     * Nombre de tags NON affichés (réellement masqués), pour le badge « +N autres ».
     * = total des tags du média − nombre de chips effectivement rendus.
     */
    get hiddenTagCount(): number {
        const total = this.media?.tags?.length ?? 0;
        return Math.max(0, total - this.visibleTagCount);
    }

    /**
     * Libellé « Expiré depuis X jours » à partir de la date de validité du média.
     * Renvoie une chaîne traduite (aujourd'hui / 1 jour / N jours).
     */
    get expiredSinceLabel(): string {
        const days = this._expiredSinceDays();
        if (days <= 0) {
            return this.translate.instant("dam.expiredToday");
        }
        if (days === 1) {
            return this.translate.instant("dam.expiredSinceDay", {days});
        }
        return this.translate.instant("dam.expiredSince", {days});
    }

    /** Nombre de jours écoulés depuis la date de validité (0 si non expiré / date absente). */
    private _expiredSinceDays(): number {
        const MS_PER_DAY = 86_400_000;
        if (!this.media?.isExpired || !this.media.dateValid) {
            return 0;
        }
        const expiry = this._parseDate(String(this.media.dateValid));
        if (!expiry) {
            return 0;
        }
        return Math.max(0, Math.floor((Date.now() - expiry.getTime()) / MS_PER_DAY));
    }

    /** Parse une date « JJ/MM/AAAA » (repli ISO) en Date locale, ou null si invalide. */
    private _parseDate(value: string): Date | null {
        const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (match) {
            return new Date(+match[3], +match[2] - 1, +match[1]);
        }
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    /**
     * Whether the media is already in the cart (drives the selection checkbox).
     */
    get isInCart(): boolean {
        return !!this.media && this.cart && this.cart.includes(this.media.id);
    }

    /** Whether the current user can rename the media (DAM update right). */
    get canRename(): boolean {
        return !!this.acl?.MAESTRO_DAM_MEDIA_UPDATE;
    }

    /**
     * Whether at least one secondary action is available to populate the kebab menu.
     */
    get hasMenuActions(): boolean {
        return !!(this.canRename || (this.buttons && (this.buttons.image || this.buttons.crop || this.buttons.delete || this.buttons.unlink)));
    }

    @Input() mode: string = "gridItem";
    @Input() poppedUp: boolean = false; // Used with dataview pop up to let to select extension

    externalLinks;
    externalText = "";

    constructor(
        private sanitizer: DomSanitizer,
        private _route: ActivatedRoute,
        private _router: Router,
        private thumbnailsService: ThumbnailsService,
        private _swalModal: SwalModalService,
        public translate: TranslateService,
        private _uploadService: UploadService,
        private _swalTargets: SwalPortalTargets,
        private _toastService: ToastService,
        private _fb: FormBuilder,
        private _tagService: TagService,
        private _damService: DataModelService,
        private _mediaService: MediaService,
        private _userService: UserService,
        private _thumbnailsService: ThumbnailsService,
        private _previewService: PreviewService,
        private _cdr: ChangeDetectorRef,
        private _ngZone: NgZone,
        private _modalService: NgbModal
    ) {}

    ngOnInit() {
        this.acl = this._userService.getUserAclFromToken();

        this._uploadService.acceptList.subscribe((acceptList) => (this.acceptList = acceptList));

        // `buttons` peut être absent : la pop-up de sélection « Lier un média »
        // (PIM + champ média) ne passe volontairement aucun bouton d'action — on
        // ne mute donc les droits que si une config de boutons est fournie.
        if (this.buttons) {
            if (("pim" === this.module && !this.acl.SYSPAD_PIM_MEDIA_READ) || ("dam" === this.module && !this.acl.SYSPAD_DAM_MEDIA_READ)) {
                this.buttons.preview = false;
            }

            if ("pim" === this.module && !this.acl.SYSPAD_PIM_MEDIA_DELETE) {
                this.buttons.unlink = false;
            }

            if ("dam" === this.module && !this.acl.MAESTRO_DAM_MEDIA_UPDATE) {
                this.buttons.crop = false;
                this.buttons.image = false;
                this.buttons.fill = false;
            }
        }

        this.formAddTag = this._fb.group({
            tag: [[], Validators.required],
        });

        this.formRename = this._fb.group({
            name: ["", Validators.required],
        });

        this.dataModelForm = this._fb.group({
            dm: [[this.media.model], Validators.required],
        });

        this.damUrl = environment.damUrl;

        if (!this.media.id && this.media.link && this.media.loadMetadata) {
            this._thumbnailsService.getUrlMetadata(this.media.link).subscribe((metadata) => {
                if (metadata.thumbPath.length) {
                    this.media.originalLink = this.media.link; // Keep original link to save in db instead of metadata link
                    this.media.link = this.media.thumbPath = metadata.thumbPath;
                }

                if (metadata.name.length) {
                    this.media.name = metadata.name;
                }
            });
        }
    }

    ngAfterViewInit(): void {
        if (!this.keywordsListRef) {
            return;
        }
        // ResizeObserver hors zone Angular : la mesure ne doit pas déclencher de CD en boucle.
        this._ngZone.runOutsideAngular(() => {
            this._keywordsResizeObserver = new ResizeObserver(() => this.computeVisibleTags());
            this._keywordsResizeObserver.observe(this.keywordsListRef.nativeElement);
        });
        this.computeVisibleTags();
    }

    ngOnDestroy(): void {
        this._keywordsResizeObserver?.disconnect();
    }

    /**
     * Calcule l'affichage de la zone mots-clés (2 lignes MAX) selon la règle :
     *
     *  - Si tous les tags (+ le bouton « + ») tiennent sur 2 lignes → on les
     *    affiche tous, à leur largeur naturelle, sans troncature.
     *  - Sinon (débordement), on REMPLIT les 2 lignes en tronquant le dernier
     *    chip de chaque ligne si besoin (« un tag de plus même si tronqué »), et
     *    le cluster « +N autres » + « + » est réservé en fin de 2e ligne — donc
     *    « +N autres » apparaît toujours sur la 2e ligne, à gauche du « + ».
     *
     * Stratégie : on rend d'abord TOUS les chips à leur largeur naturelle (pour
     * les mesurer), on simule le remplissage des 2 lignes, puis on fixe
     * `visibleTagCount` + `tagMaxWidths`. Tout est synchrone (detectChanges) →
     * l'état intermédiaire « tout affiché » n'est jamais peint.
     */
    private computeVisibleTags(): void {
        const el = this.keywordsListRef?.nativeElement;
        const tags = this.media?.tags;
        if (!el || !tags || !tags.length) {
            return;
        }

        // 1) Rendre tous les chips à leur largeur NATURELLE (sans troncature) pour mesurer.
        if (this.visibleTagCount !== tags.length || Object.keys(this.tagMaxWidths).length) {
            this.visibleTagCount = tags.length;
            this.tagMaxWidths = {};
            this._cdr.detectChanges();
        }

        const containerWidth = el.clientWidth;
        if (!containerWidth) {
            return;
        }

        const chips = Array.from(el.querySelectorAll<HTMLElement>(".tags"));
        if (!chips.length) {
            return;
        }

        const total = tags.length;
        const chipWidths = chips.map((c) => c.offsetWidth + TAG_GAP); // largeur réelle + gap

        // Pas de débordement : tous les tags + « + » tiennent sur 2 lignes → tout afficher.
        if (this.naturalLines(chipWidths, containerWidth, total) <= 2) {
            this.applyVisibleTags(total, {});
            return;
        }

        // Débordement : on remplit 2 lignes, en tronquant le dernier chip de chaque
        // ligne si besoin. La 2e ligne réserve à droite la place du cluster
        // « +N autres » + « + » → le compteur reste sur la 2e ligne, à gauche du « + ».
        const reserve = TAG_MORE_BADGE_WIDTH + TAG_GAP + (this.canAddTag ? TAG_ADD_BTN_WIDTH + TAG_GAP : 0);
        const truncations: {[index: number]: number} = {};

        // Remplit une ligne (largeur `budget`) à partir du tag `start` ; tronque le
        // dernier chip pour combler la fin de ligne s'il reste assez de place.
        // Retourne l'index du prochain tag à placer.
        const fillLine = (start: number, budget: number): number => {
            let x = 0;
            let idx = start;
            while (idx < total) {
                const w = chipWidths[idx];
                if (x + w <= budget) {
                    x += w;
                    idx++;
                    continue;
                }
                const remaining = budget - x;
                if (remaining >= TAG_MIN_CHIP_WIDTH) {
                    truncations[idx] = remaining - TAG_GAP; // tronque pour combler la fin de ligne
                    idx++;
                }
                break; // ligne pleine
            }
            return idx;
        };

        let idx = fillLine(0, containerWidth); // ligne 1 : pleine largeur
        idx = fillLine(idx, containerWidth - reserve); // ligne 2 : on réserve le cluster à droite
        this.applyVisibleTags(idx, truncations);
    }

    /**
     * Nombre de lignes occupées par `k` tags à leur largeur naturelle, suivis du
     * bouton « + » (si autorisé), sur une largeur `containerWidth`. Sert à
     * détecter le débordement (> 2 lignes) avant de basculer en mode troncature.
     */
    private naturalLines(chipWidths: number[], containerWidth: number, k: number): number {
        const widths = chipWidths.slice(0, k);
        if (this.canAddTag) {
            widths.push(TAG_ADD_BTN_WIDTH + TAG_GAP);
        }
        let lines = 1;
        let x = 0;
        for (const w of widths) {
            if (x === 0) {
                x = w; // 1er élément d'une ligne : toujours placé
            } else if (x + w <= containerWidth) {
                x += w;
            } else {
                lines++;
                x = w;
            }
        }
        return lines;
    }

    /** Applique le nombre de tags visibles + leurs largeurs de troncature, puis force le rendu. */
    private applyVisibleTags(count: number, truncations: {[index: number]: number}): void {
        this.visibleTagCount = count;
        this.tagMaxWidths = truncations;
        this._cdr.detectChanges();
    }

    /**
     * Get the base64 image
     * @param param0
     * @returns
     */
    public getThumb(thumb: { binImage: any }) {
        return this.thumbnailsService.getThumb(thumb);
    }

    /**
     * Open the preview
     */
    open(): void {
        this.openModal.emit(this.media);
    }

    /**
     * Toggle the media in/out of the cart from the selection checkbox.
     */
    toggleCart(checked: boolean): void {
        if (checked) {
            this.addCart.emit(this.media);
        } else {
            this.removeCart.emit(this.media);
        }
    }

    /**
     * Download the media file directly (DAM card action).
     */
    downloadMedia(): void {
        this._previewService.getDownloadFile(this.media.id).subscribe((res: any) => {
            if (!res || !res.data || !res.data.file) {
                return;
            }
            const blob = this._base64ToBlob(res.data.file);
            saveAs(blob, res.data.filename ? res.data.filename : this.media.name);
        });
    }

    /**
     * Convert a base64 payload into a Blob suitable for download.
     */
    private _base64ToBlob(base64: string): Blob {
        const byteChars = atob(base64);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
            bytes[i] = byteChars.charCodeAt(i);
        }
        return new Blob([bytes], {type: "application/octet-stream"});
    }

    /**
     * Redirect to the model creation
     *
     * @param id
     */
    createMedia(id: number): void {
        this.dataModelModal.fire().then((result) => {
            if (result.value) {
                if (this.dataModelForm.value.dm.length > 0) {
                    this._damService.setModel(id, this.dataModelForm.value.dm[0].id).subscribe(() => {
                        this.media.model = this.dataModelForm.value.dm[0];
                    });
                } else {
                    this._damService.setModel(id, null).subscribe(() => {
                        this.media.model = null;
                    });
                }
            }
        });
    }

    /**
     * Ouvre le formulaire d'édition du média dans une pop-up (au lieu de naviguer
     * vers la page dédiée), réutilisant app-edit-form en mode preview.
     */
    editMedia(id: number): void {
        this.editingName = false;
        this._modalService.open(this.editFormModal, {
            size: "lg",
            scrollable: true,
            windowClass: "modal-edit-media",
        });
    }

    /**
     * Delete a media
     *
     * @param id
     */
    deleteMedia(id: number): void {
        this._swalModal.deleteWithContent("modal.mediadam.delete").then((result) => {
            if (result.value) {
                this.deleted.emit(id);
            }
        });
    }

    /**
     * Emit the event to unlink a media
     * @param id
     */
    unlinkMedia(id: number): void {
        this._swalModal.delete().then((result) => {
            if (result.value) {
                this.unlinked.emit(id);
            }
        });
    }

    /**
     * Get the color for display the extension
     * @returns
     */
    getIconClass(): string {
        return this.thumbnailsService.getIconClass(this.media.extension);
    }

    /**
     * Update a media
     *
     * @param id
     */
    showPopupChangeMedia(id: number): void {
        this.changeMediaSwal.fire().then((result) => {
            if (result.value) {
                const file = ($("#newFile").get(0) as HTMLInputElement).files.item(0);
                let isPicto = false;

                if (this.acl.MAESTRO_DAM_PICTO_CREATE === 1) {
                    isPicto = $("#picto:checked").length === 1;
                }

                if (file) {
                    this.thumbnailsService
                        .updateMedia(id, file, isPicto)
                        .pipe(map((r: any) => r.data))
                        .subscribe((response) => {
                            this._toastService.show({message: this.translate.instant("dam.updated"), type: "success"});
                            this.thumbUpdateEvent.emit(id);
                        });
                }
            }
        });
    }

    /** Nom du média sans extension (pour l'édition inline du nom dans la pop-up). */
    get nameBase(): string {
        return this._baseName();
    }

    /**
     * Renomme le média depuis l'édition inline de la pop-up (extension conservée).
     *
     * @param value nouveau nom de base saisi
     */
    saveName(value: string): void {
        const base = (value || "").trim();
        if (!base) {
            this.editingName = false;
            return;
        }
        const newName = this.media.extension ? `${base}.${this.media.extension}` : base;
        this._mediaService.renameMedia(this.media.id, newName).subscribe((res: any) => {
            this.media.name = res?.data?.name ? res.data.name : newName;
            this.editingName = false;
            this._toastService.show({ message: this.translate.instant("dam.renamed"), type: "success" });
        });
    }

    /** Nom du média sans son extension (pour pré-remplir la saisie de renommage). */
    private _baseName(): string {
        const name = this.media?.name ?? "";
        const ext = this.media?.extension;
        if (ext && name.toLowerCase().endsWith("." + ext.toLowerCase())) {
            return name.slice(0, -(ext.length + 1));
        }
        return name;
    }

    /**
     * Ouvre la modale de renommage : on édite le nom de base, l'extension est
     * conservée automatiquement.
     */
    openRenameModal(media: Thumb): void {
        this.formRename.controls.name.setValue(this._baseName());
        this.renameModal.update({ title: this.translate.instant("dam.rename") });
        this.renameModal.fire().then((result) => {
            if (!result.value) {
                return;
            }
            const base = (this.formRename.value.name || "").trim();
            if (!base) {
                return;
            }
            const newName = media.extension ? `${base}.${media.extension}` : base;
            this._mediaService.renameMedia(media.id, newName).subscribe((res: any) => {
                this.media.name = res?.data?.name ? res.data.name : newName;
                this._toastService.show({ message: this.translate.instant("dam.renamed"), type: "success" });
            });
        });
    }

    /**
     * Open tag modal
     */
    openTagModal(media: Thumb): void {
        this.formAddTag.controls.tag.setValue([]);

        this.addTagModal.fire().then((result) => {
            if (result.value) {
                if (this.formAddTag.value.tag[0]) {
                    this._tagService
                        .addTag(
                            media.id,
                            this.formAddTag.value.tag.map((t) => t.id)
                        )
                        .subscribe((response: any) => {
                            this.media.tags = response.data;
                            this.refreshVisibleTags();
                        });
                }
            }
        });
        this.addTagModal.update({
            title: this.translate.instant("modal.addTag", {key: media.name}),
        });
        this.filteredTags = this.tags.filter((t) => !media.tags.map((mt) => mt.id).includes(t.id));
    }

    /**
     * Remove a tag
     */
    removeTag(media: Thumb, tagId: number): void {
        this._tagService.removeTag(media.id, {id: tagId}).subscribe((response: any) => {
            this.media.tags = response.data;
            this.tagRemoved.emit();
            this.refreshVisibleTags();
        });
    }

    /** Recalcule la zone mots-clés après mutation des tags (ajout / suppression). */
    private refreshVisibleTags(): void {
        this.visibleTagCount = this.media?.tags?.length ?? 0;
        this.tagMaxWidths = {};
        // Laisse Angular rendre les nouveaux chips avant de re-mesurer.
        setTimeout(() => this.computeVisibleTags());
    }

    /**
     * Send media by mail
     *
     * @param media
     */
    sendByMail(media: Thumb): void {
        alert("Not implemented yet");
    }

    /**
     * Open the crop modal
     *
     * @param mediaId
     */
    cropMedia(mediaId: number): void {
        this.cropper.openCrop(mediaId);
    }

    /**
     * Create a cropped image
     *
     * @param $event
     */
    croppedMedia($event: any): void {
        let flipH = $event.transform.flipH ? $event.transform.flipH : false;
        let flipV = $event.transform.flipV ? $event.transform.flipV : false;
        let crop = {
            x: $event.croppedImage.cropperPosition.x1,
            y: $event.croppedImage.cropperPosition.y1,
            height: $event.croppedImage.height,
            width: $event.croppedImage.width,
            rotate: $event.rotation,
            flipH: flipH,
            flipV: flipV,
        };
        this._mediaService.createCroppedImage(this.media.id, crop, $event.format).subscribe(() => {
        });
    }

    /**
     * Check if media exist
     * @param arrayMedias
     * @param mediaId
     * @param isObject
     * @returns
     */
    findMedia(arrayMedias: any[], mediaId: number, isObject: boolean = false): boolean {
        return arrayMedias.find((id) => (isObject ? id.id === mediaId : id === mediaId)) ? true : false;
    }

    selectedMedia(media) {
        this.selectMedia.emit(media);
    }

    openCropMedia(media) {
        this.openCrop.emit(media);
    }
}
